import { computed, inject, Injector, Service, signal } from '@angular/core';
import { User } from '@core/interfaces/user.interface';
import { Roles } from '@core/types/role.type';
import { createAuthClient } from 'better-auth/client';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@core/interfaces/api-response.interface';
import { GarageService } from '@core/services/garage/garage.service';
import { usernameClient } from 'better-auth/client/plugins';

// Kept outside the class so Angular's DI/change-detection never inspects the
// Better-Auth Proxy and accidentally triggers valueOf/toString fetches.
const authClient = createAuthClient({
  baseURL: environment.baseURL,
  plugins: [usernameClient()]
});

@Service()
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly injector = inject(Injector);

  private lastSessionSyncMs: number | null = null;
  private refreshInFlight: Promise<void> | null = null;

  private readonly _user = signal<User | undefined>(undefined);
  public readonly user = this._user.asReadonly();

  public readonly isAuthenticated = computed(() => !!this._user());
  public readonly isAdmin = computed(() => this._user()?.role === Roles.Admin);

  /**
   * Ensures we don't spam `getSession()` on every route change.
   * Adds a minimum interval + in-flight de-dupe to avoid concurrent/rapid calls.
   */
  public async ensureSessionFresh(options?: {
    force?: boolean;
    /** Minimum time between network calls (ms), even when unauthenticated. */
    minIntervalMs?: number;
  }): Promise<void> {
    const { force = false, minIntervalMs = 15 * 60 * 1000 } = options ?? {};

    const now = Date.now();
    const lastSyncMs = this.lastSessionSyncMs;

    if (this.refreshInFlight) return this.refreshInFlight;

    if (!force && lastSyncMs !== null && now - lastSyncMs < minIntervalMs) return;

    this.refreshInFlight = this._refreshSessionNetwork();
    try {
      await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  /** Forces a session refresh over the network (deduped by `ensureSessionFresh`). */
  public async refreshSession(): Promise<void> {
    await this.ensureSessionFresh({ force: true });
  }

  /** Clears local auth state without making any network calls. */
  public clearLocalSession(): void {
    this._user.set(undefined);
    this.lastSessionSyncMs = null;
    this.injector.get(GarageService).reset();
  }

  public updateUser(user: User): void {
    this._user.set(user);
  }

  public async signIn(data: { username: string; password: string; rememberMe: boolean }) {
    const result = await authClient.signIn.username(data);

    // Some cookie-based auth flows don't return a user payload here.
    // The canonical source of truth becomes getSession() (backed by cookies).
    if (!result.error) {
      await this.refreshSession();
    } else {
      this.clearLocalSession();
    }

    return result;
  }

  public signUp(data: { email: string; password: string; username: string; inviteToken: string }) {
    return this.httpClient.post<ApiResponse<User>>(`${environment.apiURL}/public/register-with-invite`, data);
  }

  public signOut() {
    return authClient.signOut().then((result) => {
      this.clearLocalSession();
      return result;
    });
  }

  public changePassword(data: { currentPassword: string; newPassword: string }) {
    return authClient.changePassword(data);
  }

  public verifyEmail(token: string) {
    return authClient.verifyEmail({ query: { token } });
  }

  public sendVerificationEmail(email: string) {
    return authClient.sendVerificationEmail({ email });
  }

  public requestPasswordReset(email: string) {
    return authClient.requestPasswordReset({ email });
  }

  public resetPassword(data: { newPassword: string; token: string }) {
    return authClient.resetPassword(data);
  }

  private async _refreshSessionNetwork(): Promise<void> {
    const now = Date.now();
    this.lastSessionSyncMs = now;

    try {
      const sessionResult = await authClient.getSession();
      const user = sessionResult.data?.user as User | undefined;

      // If not logged in, Better Auth can return no user/session.
      // Treat that as a normal "logged out" state.
      if (!user) {
        this._user.set(undefined);
        return;
      }

      this._user.set(user);
    } catch {
      this._user.set(undefined);
    }
  }
}
